// My agent › Crew and Budget (M4-06, Agents UX pass 2026-10-21; 05 §Personal crews):
// - Crew: the agents of my crew. The role default is always there; I add agents with a name and
//   the skill packs my role's policy allows (`POST crews/me/crew-agents`: the server refuses a
//   pack outside the policy with a 422 that shows inline), and remove the ones I added (hover
//   action). The pack list is the company's (`skillPacks.list`, cached with Agents › Skill packs),
//   read when "Add an agent" opens.
// - Budget: what my agent spent this period against the cap that applies (the lower of my own
//   cap and the ceiling admins set); I can lower my cap or return to the ceiling
//   (`PATCH crews/me {cap_minor}`).
import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  Checkbox,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Label,
  ListRow,
  Notice,
  Progress,
  SaveIndicator,
  SettingsSection,
  Skeleton,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { BotIcon, PackageIcon, PiggyBankIcon, Trash2Icon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import { RowAction } from "../agents/agents-ux";
import { skillPacksQuery } from "../agents/skill-packs-screen";
import type { CompanyAccess } from "../company";
import { crewKeys } from "../queries";
import { problemText } from "../settings/problem-text";

type CrewView = OperationOutput<"crews.me">;
type CrewAgent = CrewView["crew_agents"][number];
type Pack = OperationOutput<"skillPacks.list">["data"][number];

const SOURCE_LABELS: Record<CrewAgent["source"], string> = {
  role_default: "Role default",
  owner_added: "Added by you",
};

export function CrewAgentsSection({
  api,
  company,
  view,
}: {
  api: Api;
  company: CompanyAccess;
  view: CrewView;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const refresh = () => void qc.invalidateQueries({ queryKey: crewKeys.me(co) });
  const remove = useMutation({
    mutationFn: (id: string) => api.client.crews.removeAgent({ params: { co, id } }),
    onSuccess: refresh,
  });
  const packs = useInfiniteQuery({ ...skillPacksQuery(api, co), enabled: adding });
  const packRows = packs.data?.pages.flatMap((p) => p.data) ?? [];
  const nameOf = (id: string) => packRows.find((p) => p.id === id)?.name ?? null;
  return (
    <SettingsSection
      id="crew"
      icon={BotIcon}
      title="Crew"
      actions={
        adding ? null : (
          <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
            Add an agent
          </Button>
        )
      }
    >
      <div data-slot="crew-agents" className="rounded-lg border border-border">
        {view.crew_agents.map((a) => (
          <ListRow
            key={a.id}
            data-row
            data-crew-agent={a.id}
            lead={<BotIcon aria-hidden="true" />}
            title={a.name}
            chips={
              <>
                <Badge variant={a.source === "role_default" ? "neutral" : "accent"}>
                  {SOURCE_LABELS[a.source]}
                </Badge>
                {a.skill_pack_ids.map((id) => (
                  <Badge key={id} variant="outline" className="max-md:hidden">
                    <PackageIcon aria-hidden="true" className="size-3" />
                    {nameOf(id) ?? "Skill pack"}
                  </Badge>
                ))}
              </>
            }
            meta={
              a.skill_pack_ids.length
                ? `${a.skill_pack_ids.length} skill pack${a.skill_pack_ids.length === 1 ? "" : "s"}`
                : "No skill packs"
            }
            actions={
              a.source === "owner_added" ? (
                <RowAction
                  icon={Trash2Icon}
                  label={`Remove ${a.name}`}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(a.id)}
                />
              ) : null
            }
          />
        ))}
      </div>
      {remove.isError ? (
        <Notice tone="danger" role="alert">
          {problemText(remove.error, "The agent could not be removed.")}
        </Notice>
      ) : null}
      {adding ? (
        packs.isPending ? (
          <div role="status" aria-label="Loading skill packs" className="flex flex-col gap-2">
            <Skeleton className="h-10" />
          </div>
        ) : (
          <AddAgentForm
            api={api}
            co={co}
            packs={packRows}
            onDone={() => {
              setAdding(false);
              refresh();
            }}
            onCancel={() => setAdding(false)}
          />
        )
      ) : null}
    </SettingsSection>
  );
}

function AddAgentForm({
  api,
  co,
  packs,
  onDone,
  onCancel,
}: {
  api: Api;
  co: string;
  packs: Pack[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const baseId = useId();
  const [name, setName] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const add = useMutation({
    mutationFn: () =>
      api.client.crews.addAgent({
        params: { co },
        body: { name: name.trim(), skill_pack_ids: [...picked] },
      }),
    onSuccess: onDone,
  });
  return (
    <form
      aria-label="Add an agent"
      className="flex flex-col gap-4 rounded-lg border border-border bg-sunken p-4"
      data-slot="add-crew-agent"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) add.mutate();
      }}
    >
      <Field>
        <FieldLabel htmlFor={`${baseId}-name`}>Agent name</FieldLabel>
        <Input
          id={`${baseId}-name`}
          value={name}
          maxLength={120}
          placeholder="e.g. Sourcing helper"
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <fieldset className="flex flex-col gap-2" data-slot="add-crew-packs">
        <legend className="mb-1 text-sm font-medium">Skill packs</legend>
        {packs.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            The company has no skill packs yet; the agent starts with the role's defaults.
          </p>
        ) : (
          packs.map((p) => {
            const id = `${baseId}-${p.id}`;
            return (
              <div key={p.id} className="flex items-center gap-2">
                <Checkbox
                  id={id}
                  aria-label={p.name}
                  checked={picked.has(p.id)}
                  onCheckedChange={(on) =>
                    setPicked((cur) => {
                      const next = new Set(cur);
                      if (on === true) next.add(p.id);
                      else next.delete(p.id);
                      return next;
                    })
                  }
                />
                <Label htmlFor={id} className="font-normal">
                  {p.name}
                  <span className="ml-2 text-xs text-muted-foreground">v{p.version}</span>
                </Label>
              </div>
            );
          })
        )}
        <FieldDescription>
          Only packs your role's policy allows are accepted; the rest are refused when you add.
        </FieldDescription>
      </fieldset>
      <div className="flex items-center gap-3">
        <Button type="submit" size="sm" disabled={!name.trim() || add.isPending}>
          {add.isPending ? "Adding…" : "Add agent"}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {add.isError ? (
        <Notice tone="danger" role="alert">
          {problemText(add.error, "The agent could not be added.")}
        </Notice>
      ) : null}
    </form>
  );
}

const money = (minor: number, currency: string) => `${(minor / 100).toFixed(2)} ${currency}`;

export function CrewBudgetSection({ api, co, view }: { api: Api; co: string; view: CrewView }) {
  const qc = useQueryClient();
  const { caps } = view;
  const [cap, setCap] = useState(caps.cap_minor === null ? "" : (caps.cap_minor / 100).toFixed(2));
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const save = useMutation({
    mutationFn: (cap_minor: number | null) =>
      api.client.crews.update({ params: { co }, body: { cap_minor } }),
    onSuccess: (v) => {
      qc.setQueryData(crewKeys.me(co), v);
      setCap(v.caps.cap_minor === null ? "" : (v.caps.cap_minor / 100).toFixed(2));
      setSavedAt(Date.now());
    },
  });
  const parsed = cap.trim() === "" ? null : Math.round(Number(cap) * 100);
  const valid = parsed === null || (Number.isFinite(parsed) && parsed >= 0);
  const dirty = parsed !== caps.cap_minor;
  const used =
    caps.effective_minor && caps.effective_minor > 0
      ? Math.min(100, Math.round((caps.spent_minor / caps.effective_minor) * 100))
      : null;
  return (
    <SettingsSection id="budget" icon={PiggyBankIcon} title="Budget">
      <div className="flex flex-col gap-2" data-slot="crew-budget">
        <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
          <span>
            Spent this {caps.period_type}:{" "}
            <strong className="tabular-nums">{money(caps.spent_minor, caps.currency)}</strong>
            {caps.effective_minor === null
              ? " · no cap"
              : ` of ${money(caps.effective_minor, caps.currency)}`}
          </span>
          <span className="text-xs text-muted-foreground">
            Ceiling{" "}
            {caps.ceiling_minor === null ? "none" : money(caps.ceiling_minor, caps.currency)}
          </span>
        </div>
        {used !== null ? (
          <Progress value={used} aria-label="Budget used" data-slot="crew-budget-bar" />
        ) : null}
      </div>
      <form
        aria-label="Budget"
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid && dirty) save.mutate(parsed);
        }}
      >
        <Field className="max-w-xs">
          <FieldLabel htmlFor="crew-cap">
            My cap per {caps.period_type} ({caps.currency})
          </FieldLabel>
          <Input
            id="crew-cap"
            inputMode="decimal"
            value={cap}
            placeholder={caps.ceiling_minor === null ? "No cap" : "Use the ceiling"}
            aria-invalid={valid ? undefined : true}
            onChange={(e) => setCap(e.target.value)}
          />
          <FieldDescription>
            Empty means the ceiling applies. A cap can only lower it; a reached cap pauses your
            agent for the rest of the period.
          </FieldDescription>
        </Field>
        <div className="flex items-center gap-3">
          <Button type="submit" size="sm" disabled={!valid || !dirty || save.isPending}>
            {save.isPending ? "Saving…" : "Save budget"}
          </Button>
          <SaveIndicator savedAt={savedAt} />
        </div>
        {save.isError ? (
          <Notice tone="danger" role="alert">
            {problemText(save.error, "The budget could not be saved.")}
          </Notice>
        ) : null}
      </form>
    </SettingsSection>
  );
}
